
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ArrowRight, X } from 'lucide-react';

const DateRangePicker = ({
  startDate = null,
  endDate = null,
  onChange,
  className = '',
  defaultRange = null
}) => {
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const yesterday = new Date(
    new Date(new Date().setDate(today.getDate() - 1)).setHours(0, 0, 0, 0)
  );

  const ranges = [
    {
      title: "Today",
      alias: "today",
      period: { since: today, until: today },
    },
    {
      title: "Yesterday",
      alias: "yesterday",
      period: { since: yesterday, until: yesterday },
    },
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 30)).setHours(0, 0, 0, 0)),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 90)).setHours(0, 0, 0, 0)),
        until: today,
      },
    },
    {
      title: "Last 6 Months",
      alias: "last6months",
      period: {
        since: new Date(new Date(new Date().setMonth(today.getMonth() - 6)).setHours(0, 0, 0, 0)),
        until: today,
      },
    },
    {
      title: "Last 1 Year",
      alias: "last1year",
      period: {
        since: new Date(new Date(new Date().setFullYear(today.getFullYear() - 1)).setHours(0, 0, 0, 0)),
        until: today,
      },
    },
    {
      title: "Life-time",
      alias: "lifetime",
      period: {
        since: new Date(new Date("2020-01-01").setHours(0, 0, 0, 0)), // Assuming a start date
        until: today,
      },
    },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [activeDateRange, setActiveDateRange] = useState(null);
  const [inputValues, setInputValues] = useState({ since: '', until: '' });
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedStart, setSelectedStart] = useState(startDate);
  const [selectedEnd, setSelectedEnd] = useState(endDate);
  const [hoveredDate, setHoveredDate] = useState(null);

  const containerRef = useRef(null);

  const formatDateDisplay = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Auto position balancing for desktop
  const adjustPosition = () => {
    if (!containerRef.current) return;

    const trigger = containerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const DROPDOWN_W = 560;
    const DROPDOWN_H = 420;

    let top = trigger.bottom + 8;
    let left = trigger.left;

    if (vh - trigger.bottom < DROPDOWN_H) {
      top = trigger.top - DROPDOWN_H - 8;
    }
    if (vw - trigger.left < DROPDOWN_W) {
      left = vw - DROPDOWN_W - 12;
    }

    if (left < 12) left = 12;
    if (top < 12) top = 12;

    setDropdownStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 50,
      width: DROPDOWN_W,
    });
  };

  useEffect(() => {
    if (isOpen && window.innerWidth >= 768) {
      adjustPosition();
      window.addEventListener('resize', adjustPosition);
      window.addEventListener('scroll', adjustPosition);
    }
    return () => {
      window.removeEventListener('resize', adjustPosition);
      window.removeEventListener('scroll', adjustPosition);
    };
  }, [isOpen]);

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart(clickedDate);
      setSelectedEnd(null);
      setActiveDateRange(null);
    } else if (selectedStart && !selectedEnd) {
      if (clickedDate >= selectedStart) {
        setSelectedEnd(clickedDate);
        setActiveDateRange({
          alias: "custom",
          title: "Custom",
          period: { since: selectedStart, until: clickedDate },
        });
      } else {
        setSelectedStart(clickedDate);
        setSelectedEnd(null);
        setActiveDateRange(null);
      }
    }
  };

  const handleRangeSelect = (range) => {
    setActiveDateRange(range);
    setSelectedStart(range.period.since);
    setSelectedEnd(range.period.until);
    setCurrentMonth(range.period.since.getMonth());
    setCurrentYear(range.period.since.getFullYear());
  };

  // Initialize with defaultRange
  useEffect(() => {
    const storedRange = sessionStorage.getItem('defaultDateRange');
    let hasDefaultRange = null;

    if (storedRange) {
      try {
        const parsed = JSON.parse(storedRange);
        const matchedRange = ranges.find(r => r.alias === parsed.alias);
        
        if (matchedRange) {
          hasDefaultRange = matchedRange;
        } else if (parsed.since && parsed.until) {
          hasDefaultRange = {
            alias: "custom",
            title: "Custom",
            period: {
              since: new Date(parsed.since),
              until: new Date(parsed.until)
            }
          };
        }
      } catch (e) {
        console.error('Error parsing stored range:', e);
      }
    } else if (defaultRange) {
      const matchedRange = ranges.find(r => r.alias === defaultRange);
      if (matchedRange) {
        hasDefaultRange = matchedRange;
      }
    }

    if (hasDefaultRange) {
      setActiveDateRange(hasDefaultRange);
      setSelectedStart(hasDefaultRange.period.since);
      setSelectedEnd(hasDefaultRange.period.until);
      setCurrentMonth(hasDefaultRange.period.since.getMonth());
      setCurrentYear(hasDefaultRange.period.since.getFullYear());

      if (typeof onChange === 'function') {
        onChange(hasDefaultRange.period.since, hasDefaultRange.period.until);
      }
    } else {
      setActiveDateRange(null);
      setSelectedStart(startDate);
      setSelectedEnd(endDate);
    }
  }, [defaultRange]);

  const handleApply = () => {
    setIsOpen(false);
    if (typeof onChange === 'function') {
      const rangeData = activeDateRange?.alias && activeDateRange.alias !== 'custom'
        ? { alias: activeDateRange.alias }
        : { since: selectedStart, until: selectedEnd };
      
      sessionStorage.setItem('defaultDateRange', JSON.stringify(rangeData));
      onChange(selectedStart || null, selectedEnd || null);
    }
  };

  const handleClear = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
    setActiveDateRange(null);
    setInputValues({ since: '', until: '' });
    sessionStorage.removeItem('defaultDateRange');
    if (typeof onChange === 'function') {
      onChange(null, null);
    }
    setIsOpen(false);
  };

  const isDateInRange = (day) => {
    if (!selectedStart) return false;
    const date = new Date(currentYear, currentMonth, day);

    if (selectedEnd) {
      return date >= selectedStart && date <= selectedEnd;
    } else if (hoveredDate && hoveredDate >= selectedStart) {
      return date >= selectedStart && date <= hoveredDate;
    }

    return date.getTime() === selectedStart.getTime();
  };

  const isDateStart = (day) => {
    if (!selectedStart) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedStart.getTime();
  };

  const isDateEnd = (day) => {
    if (!selectedEnd) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedEnd.getTime();
  };

  const isToday = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    return date.toDateString() === today.toDateString();
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8 md:w-9 md:h-9"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isInRange = isDateInRange(day);
      const isStart = isDateStart(day);
      const isEnd = isDateEnd(day);
      const isTodayDate = isToday(day);

      days.push(
        <button
          key={day}
          className={`
            w-8 h-8 md:w-9 md:h-9 text-sm rounded-md flex items-center justify-center
            ${isStart || isEnd
              ? 'bg-black text-white'
              : isInRange
                ? 'bg-gray-100 text-gray-700'
                : isTodayDate
                  ? 'bg-gray-100 text-gray-700 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50'
            }
            transition-colors duration-150
          `}
          onClick={() => handleDateClick(day)}
          onMouseEnter={() => {
            if (selectedStart && !selectedEnd) {
              setHoveredDate(new Date(currentYear, currentMonth, day));
            }
          }}
          onMouseLeave={() => setHoveredDate(null)}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const buttonValue = (() => {
    if (selectedStart && selectedEnd) {
      return `${formatDateDisplay(selectedStart)} - ${formatDateDisplay(selectedEnd)}`;
    }
    if (selectedStart && !selectedEnd) {
      return `${formatDateDisplay(selectedStart)} - …`;
    }
    if (!selectedStart && !selectedEnd && activeDateRange) {
      return activeDateRange.title;
    }
    return 'Select dates';
  })();

  useEffect(() => {
    if (activeDateRange && activeDateRange.period) {
      setInputValues({
        since: formatDateDisplay(activeDateRange.period.since),
        until: formatDateDisplay(activeDateRange.period.until),
      });
    } else if (selectedStart && selectedEnd) {
      setInputValues({
        since: formatDateDisplay(selectedStart),
        until: formatDateDisplay(selectedEnd),
      });
    } else if (selectedStart && !selectedEnd) {
      setInputValues({
        since: formatDateDisplay(selectedStart),
        until: '',
      });
    } else {
      setInputValues({ since: '', until: '' });
    }
  }, [activeDateRange, selectedStart, selectedEnd]);

  useEffect(() => {
    if (startDate !== selectedStart) {
      setSelectedStart(startDate || null);
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate !== selectedEnd) {
      setSelectedEnd(endDate || null);
    }
  }, [endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center h-10 md:h-12 px-4 md:px-6 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:ring-offset-0 min-w-[140px] ${className}`}
      >
        <Calendar className="w-4 h-4 mr-2 text-gray-600 flex-shrink-0" />
        <span className="truncate">{buttonValue}</span>
      </button>

      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black/30 md:hidden z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            style={window.innerWidth >= 768 ? dropdownStyle : {}}
            className="fixed md:absolute inset-x-0 bottom-0 md:inset-auto
              bg-white rounded-t-xl md:rounded-lg shadow-xl border border-gray-200 z-50
              w-full md:w-auto"
          >
            <div className="flex flex-col md:flex-row max-h-[90vh] md:max-h-none overflow-y-auto">
              {/* Presets sidebar */}
              <div className="grid grid-cols-2 md:block md:w-40 bg-gray-50 p-3 md:rounded-l-lg border-b md:border-b-0 md:border-r gap-2 md:gap-0 md:space-y-1">
                {ranges.map((range) => (
                  <button
                    key={range.alias}
                    onClick={() => handleRangeSelect(range)}
                    className={`
                      w-full text-left px-3 py-2 text-sm rounded-md transition-colors font-medium
                      ${activeDateRange?.alias === range.alias
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    {range.title}
                  </button>
                ))}
              </div>

              {/* Calendar section */}
              <div className="flex-1 p-4">
                {/* Date inputs */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1">
                    <input
                      type="date"
                      value={inputValues.since || ''}
                      onChange={(e) => {
                        setInputValues(prev => ({ ...prev, since: e.target.value }));
                        if (e.target.value) {
                          const date = parseDate(e.target.value);
                          setSelectedStart(date);
                          setCurrentMonth(date.getMonth());
                          setCurrentYear(date.getFullYear());
                          if (selectedEnd && date > selectedEnd) {
                            setSelectedEnd(null);
                          }
                          setActiveDateRange(null);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <input
                      type="date"
                      value={inputValues.until || ''}
                      onChange={(e) => {
                        setInputValues(prev => ({ ...prev, until: e.target.value }));
                        if (e.target.value) {
                          const date = parseDate(e.target.value);
                          setSelectedEnd(date);
                          if (selectedStart && date < selectedStart) {
                            setSelectedStart(date);
                            setSelectedEnd(selectedStart);
                          }
                          setActiveDateRange(null);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>

                {/* Calendar */}
                <div className="bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => navigateMonth('prev')}
                      className="p-2 hover:bg-gray-100 rounded-md"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <h3 className="text-sm font-semibold text-gray-700">
                      {monthNames[currentMonth]} {currentYear}
                    </h3>
                    <button
                      onClick={() => navigateMonth('next')}
                      className="p-2 hover:bg-gray-100 rounded-md"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map((day) => (
                      <div
                        key={day}
                        className="w-8 h-8 md:w-9 md:h-9 text-xs font-medium text-gray-500 flex items-center justify-center"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendarDays()}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t">
                  <button
                    onClick={handleClear}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    title="Clear dates"
                  >
                    <X className="w-4 h-4" /> Clear
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsOpen(false)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApply}
                      className="px-3 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
